<template>

  <div class="ob-content">

    <loader :condition="dataLoaded">
      <toolbar>
        <template v-slot:head>
          <span v-if="item.asset">
            <a @click="$router.push({name: 'assets/id', params: {id: item.assetId}})">
              <span style="font-weight: 300">{{ item.asset.fullPath }}</span>
            </a>
            {{ `>` }}
          </span>
          {{ item.id ? `${$tc('issues.entity-name', 1)} #${item.id}` : $t('issues.new') }}
        </template>

        <template v-slot:controls>
          <v-btn color="primary" @click="solve()" v-if="interventions.length && !item.closed">{{ $t('interventions.solve-issue') }}</v-btn>
          <v-btn color="primary" @click="saveIssue()">{{ $t('save') }}</v-btn>
          <v-btn @click="$router.go(-1)">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" @click="onDestroy(item)" v-if="canArchive">{{ $t('archive') }}</v-btn>
        </template>
      </toolbar>

      <div class="ob-grid">
        <v-card class="ob-col-6">
          <v-combobox
            v-model="selectedIssueCode"
            :items="issueCodes"
            :label="$t('issue-codes.code')"
            :error-messages="itemErrors.code"
            :search-input.sync="searchIssueCode"
            :filter="filterIssueCodes"
            hide-selected
            clearable
            @keyup.8.prevent="selectedIssueCode = null"
            @blur="onCodeBlur"
            menu-props="closeOnContentClick"
            data-test="new-issue-code"
          >
          <template slot="no-data">
            <v-list-item>
              <span class="subtitle-1">{{ noDataValue }}</span>
            </v-list-item>
          </template>
          <template slot="item" slot-scope="{ index, item }">
            <v-list-item-content>
              <span class="subtitle-1">[{{ item.code }}] {{ item.name }}</span>
            </v-list-item-content>
          </template>
          <template slot="selection" slot-scope="{ item }">{{ item.code }}</template>
        </v-combobox>

        <v-textarea
          v-model="item.name"
          :label="$t('problem-description')"
          :rows="5"
          auto-grow
          data-test="new-issue-description"
        ></v-textarea>
      </v-card>

        <v-card class="ob-col-6">
          <div class="d-flex flex-form" v-if="item.alarm">
            <static-field :label="$t('alarms.detected-at')">{{ item.alarm.detectedAt | datetime }}</static-field>
          </div>

          <div class="d-flex flex-form">
            <v-text-field v-model="item.reportedBy" :label="$t('reported-by')" data-test="reported-by"></v-text-field>
            <date-time-picker v-model="item.actionAt" :label="$t('issues.action')" :as-object="true" data-test="action-at"></date-time-picker>
          </div>

          <v-select v-model="item.userGroupId" :label="$tc('user-group.entity-name', 1)" :items="groups" clearable data-test="new-issue-user-group"></v-select>

          <div class="d-flex align-center flex-form">
            <v-select :items="issueTypes"
                      :value="issueType"
                      @input="issueTypeChanged($event)"
                      item-text="label"
                      item-value="value"
                      :label="$t('type')"></v-select>

            <div class="green--text text-center" v-if="item.closed">
              <font-awesome-icon icon="check"></font-awesome-icon> {{ $t('issues.solved') }}
              <v-btn text color="primary" @click="reopen()">{{ $t('issues.reopen') }}</v-btn>
            </div>

            <div class="red--text text-center" v-if="!item.closed && interventions.length">
              <font-awesome-icon icon="times"></font-awesome-icon> {{ $t('issues.unresolved') }}
            </div>

            <v-alert text type="error" v-if="!interventions.length">{{ $t('issues.cannot-close') }}</v-alert>
          </div>

          <div class="d-flex flex-form" v-if="$cans('prg')">
            <v-radio-group v-model="item.category" :label="$t('category')">
              <v-radio label="Elektronika" value="E"></v-radio>
              <v-radio label="Mechanika" value="M"></v-radio>
            </v-radio-group>

            <v-radio-group v-model="item.account" :label="$t('account')">
              <v-radio label="Záruka" value="záruka"></v-radio>
              <v-radio label="Paušál" value="paušál"></v-radio>
              <v-radio label="Fakturace" value="fakturace"></v-radio>
            </v-radio-group>
          </div>
        </v-card>

        <v-card class="ob-col-12" v-if="item.alarms && item.alarms.length">
          <toolbar medium>
            <template v-slot:head>
              {{ $t('alarms') }}
            </template>
          </toolbar>

          <alarms-table :source="() => item.alarms"></alarms-table>
        </v-card>

        <interventions-tabs class="ob-col-12" :issue="item" :interventions="interventions" @change="interventionChanged($event)"></interventions-tabs>
      </div>
    </loader>
  </div>

</template>

<script>
import displayHelpers from '../helpers/display-helpers.es6'
import itemHelpers from '../helpers/item-helpers.es6'
import InterventionsTabs from './components/interventions-tabs'
import DateTimePicker from '../shared/components/date-time-picker.new'
import Toolbar from '../shared/toolbar'
import Loader from '../shared/loader'
import StaticField from '@/observer/shared/components/static-field'
import AlarmsTable from '../alarms/alarms-table'

export default {
  mixins: [displayHelpers, itemHelpers],

  data: function () {
    return {
      model: { name: 'issues' },
      auxModels: [
        { name: 'issue-codes' },
        { name: 'checklists' },
        { name: 'users' },
        { name: 'user-groups' },
        { name: 'products' }
      ],
      selectedIssueCode: null,
      searchIssueCode: null,
      issueTypes: [
        { value: 'failure', label: this.$t('failure') },
        { value: 'maintenance', label: this.$t('maintenance') }
      ],
    }
  },

  computed: {
    issueCodes () {
      return this.$store.state.items['issue-codes']
    },

    issueType () {
      return !!this.item.failure ? 'failure' : 'maintenance'
    },

    interventions () {
      return (this.item.interventions || []).slice().sort((a, b) => new Date(a.driveStartAt) - new Date(b.driveStartAt))
    },

    groups () {
      return (this.$store.state.items['user-groups'] || [])
        .map(group => ({
          text: group.name,
          value: group.id
        }))
    },

    canArchive () {
      if (!this.item.id || this.item.xHasIntervention)
        return false

      return this.$can('manager') || (this.$can('worker') && !this.item.closed)
    },

    // TODO: refactor
    noDataValue () {
      if (this.selectedIssueCode && this.selectedIssueCode.code) {
        if (!!this.selectedIssueCode.name || this.selectedIssueCode.name === '') {
          return `[${this.selectedIssueCode.code}] ${this.selectedIssueCode.name}`
        }
        return `${this.$t('create')} "${this.selectedIssueCode.code}"`
      }
      return `${this.$t('create')} "${this.searchIssueCode}"`
    }
  },

  methods: {
    issueTypeChanged (value) {
      this.item.failure = value === 'failure'
      this.item.maintenance = value === 'maintenance'
    },

    async interventionChanged (options) {
      await this.saveIssue(options.route, options.spinner)
      this.storeUpdate()
    },

    async saveIssue (reRoute = true, spinner = true) {
      return this.save({ reRoute: reRoute, snackbar: false, spinner: false })
    },

    async solve () {
      const interventions = this.interventions.filter(intervention => !intervention.workEndAt).map(intervention => this.$store.dispatch('updateItem', {
        modelName: 'interventions',
        item: Object.assign(intervention, { workEndAt: new Date() })
      }))

      await Promise.all(interventions)

      this.item.closed = true
      this.interventionChanged({ spinner: true, route: false })
      this.$router.push({ name: 'issues' })
    },

    async reopen () {
      this.item.closed = false
      this.interventionChanged({ spinner: true, route: false })
    },

    // TODO: refactor
    onCodeBlur () {
      if (this.selectedIssueCode && this.selectedIssueCode.code) {
        this.item.code = this.selectedIssueCode.code
      }

      if (!this.item.name) {
        const issueCode = this.issueCodes.find(i => i.code === this.item.code)
        if (issueCode) {
          // Must be done via store as otherwise does not work for /new (having item just {})
          this.$store.commit('modifyItem', {
            modelName: this.model.name,
            col: 'name',
            val: issueCode.name
          })
        }
      }
    },

    // TODO: refactor
    filterIssueCodes (item, queryText, itemText) {
      const actualItemText = `[${itemText.code}] ${itemText.name}`.toLowerCase()
      return actualItemText.indexOf(queryText.toString().toLowerCase()) > -1
    },

    // TODO: refactor
    reRoute () {
      const reRouteDestination = this.item.assetId ? {
        name: `assets/id`,
        params: { id: this.item.assetId }
      } : { name: `issues` }
      if (reRouteDestination.name) {
        this.$router.push(reRouteDestination)
      }
    }
  },

  watch: {
    // TODO: refactor
    selectedIssueCode (code) {
      if (typeof code === 'string') {
        this.selectedIssueCode = {
          code
        }
      }
    },

    dataLoaded (loaded) {
      if (loaded && this.item.code)
        this.selectedIssueCode = `${this.item.code}`

      // set reported by for new issues
      if (loaded && this.item.id === undefined) {
        let { id } = this.$store.state.auth.currentUser
        let user = this.$store.state.items.users.filter(u => u.id === id).shift()
        if (user) {
          this.item.reportedBy = user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : `${user.code}`
        }
      }
    }
  },

  components: {
    AlarmsTable,
    StaticField,
    Loader,
    Toolbar,
    DateTimePicker,
    InterventionsTabs
  }
}
</script>

<style scoped lang="scss">


</style>
